import { useCallback, useState } from 'react';
import type { Path, UseFormReturn } from 'react-hook-form';
import type { WizardFormValues } from '../schemas';

export type StepIndex = 0 | 1 | 2 | 3;
export const TOTAL_STEPS = 4 as const;

export const STEP_FIELDS: Record<StepIndex, Array<Path<WizardFormValues>>> = {
  0: ['site', 'length', 'width'],
  1: ['loadType', 'height', 'fireProtection'],
  2: ['phoneNumber', 'email', 'turnstile'],
  3: [],
};

export const STEP_TITLES: Record<StepIndex, string> = {
  0: 'Floor size',
  1: 'Purpose',
  2: 'Details',
  3: 'Your budget',
};

export interface WizardController {
  currentStep: StepIndex;
  goNext: () => Promise<boolean>;
  goBack: () => void;
  reset: () => void;
}

export function useWizard(
  form: UseFormReturn<WizardFormValues>,
  onLastStepBeforeResult?: () => Promise<boolean> | boolean,
): WizardController {
  const [currentStep, setCurrentStep] = useState<StepIndex>(0);

  const goNext = useCallback(async (): Promise<boolean> => {
    const ok = await form.trigger(STEP_FIELDS[currentStep]);
    if (!ok) return false;

    // Step 3 → 4 fires the contact-gate submit; only advance on success.
    if (currentStep === 2 && onLastStepBeforeResult) {
      const submitted = await onLastStepBeforeResult();
      if (!submitted) return false;
    }

    setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS - 1) as StepIndex);
    return true;
  }, [currentStep, form, onLastStepBeforeResult]);

  const goBack = useCallback(() => {
    setCurrentStep((s) => {
      // From the result page, Back skips the contact step to avoid re-prompting.
      if (s === TOTAL_STEPS - 1) return 1;
      return Math.max(s - 1, 0) as StepIndex;
    });
  }, []);

  const reset = useCallback(() => setCurrentStep(0), []);

  return { currentStep, goNext, goBack, reset };
}
